import { useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TFunction } from 'react-i18next';
import { AppContext } from '../../../context/ContextProvider';
import { getRegions } from '../../../helpers';
import { sumBy, toInternalId } from '../../../utils';
import { fetchCellarStats } from '../services/fetchMyCellar';
import { fetchCurrentHoldingVT2 } from '../../Portfolio/components/Details/services/fetchCurrentHoldings';
import { fetchCurrentAllocations } from '../../Portfolio/components/Allocation/services/fetchCurrentAllocations';

export interface RegionAllocation {
  id: string;
  name: string;
  color: string;
  textColor: string;
  count: number;
  bottles: number;
  percentage: number;
  value: number;
}

export interface CellarStats {
  totalValue: number;
  totalCost: number;
  profitAndLoss: number;
  profitAndLossPercentage: number;
  cases: number;
  bottles: number;
  uniqueWines: number;
  regionAllocations: RegionAllocation[];
  isLoading?: boolean;
}

export const useCellarStats = (t: TFunction, portfolioId?: number | null): CellarStats => {
  // Use -1 for "All portfolios" when portfolioId is null/undefined
  const effectivePortfolioId = portfolioId ?? -1;
  const {
    state: {
      settings: { currency },
    },
  } = useContext(AppContext);

  // Fetch bottle/case/region counts from API
  const { data: apiStats, isLoading } = useQuery({
    queryKey: ['cellarStats', effectivePortfolioId],
    queryFn: () => fetchCellarStats(effectivePortfolioId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch current holdings — the SAME source as the Portfolio "Portfolio Valuation" total —
  // so the Cellar total value matches the Portfolio screen exactly. These values are already
  // converted to the customer's target currency by the backend (valuePerUnit * currentExchangeRate).
  const { data: currentHoldings, isLoading: loadingHoldings } = useQuery({
    queryKey: ['cellarCurrentHoldingsValuation', { currency, portfolioId: portfolioId ?? 'all' }],
    queryFn: () => fetchCurrentHoldingVT2(portfolioId ? { portfolioId } : undefined),
    staleTime: 5 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  // Fetch the same allocation API used by Home/Portfolio for consistent percentages
  const { data: allocationData } = useQuery({
    queryKey: ['fetchCurrentAllocations'],
    queryFn: fetchCurrentAllocations,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    // Totals derived from current holdings — identical basis to the Portfolio "Portfolio Valuation".
    const holdings = currentHoldings?.data ?? [];
    const totalValue = sumBy(holdings, 'totalValue');
    const totalCost = sumBy(holdings, 'totalCost');
    const profitAndLoss = totalValue - totalCost;
    const profitAndLossPercentage = totalCost > 0 ? (profitAndLoss / totalCost) * 100 : 0;

    // Bottle/case/unique counts still come from the cellar stats API
    const bottles = apiStats?.totalCases ?? 0;
    const cases = apiStats?.totalBottle ?? 0;
    // Use uniqueLwin18 from API for unique wines count
    const uniqueWines = apiStats?.uniqueLwin18 ?? 0;

    // Build allocation percentage lookup from PortfolioCurrentAllocation API (same source as Home)
    const allocationMap = new Map<string, number>();
    (allocationData?.portfolioCurrentAllocation || []).forEach((a) => {
      allocationMap.set(toInternalId(a.regionName?.toLowerCase() || ''), a.currentAllocation || 0);
    });

    // Build regional allocations from API regionStats with value-based percentages from Home API
    let regionAllocations: RegionAllocation[] = [];
    const regions = getRegions(t);

    if (apiStats?.regionStats && apiStats.regionStats.length > 0) {
      regionAllocations = apiStats.regionStats
        .map((apiRegion) => {
          // Find matching region config for colors
          const regionKey = toInternalId(apiRegion.region?.toLowerCase() || 'other');
          const regionConfig = regions.find((r) => r.id === regionKey) || {
            id: regionKey,
            text: apiRegion.region,
            color: '#757587',
            textColor: 'white',
          };

          return {
            id: regionKey,
            name: apiRegion.region,
            color: regionConfig.color,
            textColor: regionConfig.textColor || 'white',
            count: apiRegion.totalCount,
            bottles: 0,
            percentage: allocationMap.get(regionKey) || 0,
            value: 0,
          };
        })
        .filter((region) => region.count > 0)
        .sort((a, b) => b.percentage - a.percentage);
    }

    return {
      totalValue,
      totalCost,
      profitAndLoss,
      profitAndLossPercentage,
      cases,
      bottles,
      uniqueWines,
      regionAllocations,
      isLoading: isLoading || loadingHoldings,
    };
  }, [t, apiStats, currentHoldings, isLoading, loadingHoldings, allocationData]);
};
